// @ts-nocheck
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import React, { createContext, FC, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

export interface StepperStepStatus {
    /** Indicates whether the step is currently active. */
    isActive?: boolean;
    /** Indicates whether the step has been completed. */
    isComplete?: boolean;
    /**
     * Indicates that the step is disabled and cannot be interacted with.
     * A step will be disabled when another step is being edited outside of its chronological order.
     */
    isDisabled?: boolean;
    /** Indicates that the step is being edited outside of its chronological order. */
    isEditing?: boolean;
    /** Indicates that the step has never been reached by the user yet. */
    isPending?: boolean;
}

export type StepperStepStatuses = Array<StepperStepStatus | null>;

export interface StepperContextModel {
    /** Indicates whether all steps have been completed. */
    allStepsCompleted: boolean;
    /** Function to edit a given step. */
    editStep: (step: number) => void;
    /** Function to navigate to the next step. */
    nextStep: () => void;
    /** Function to navigate to the previous step. */
    previousStep: () => void;
    /** Function to register a new step, returns the dynamically generated step number. */
    registerStep: () => number;
    /** Function to save the current step and go back to the last active step. */
    saveStep: () => void;
    /** Total number of steps in the stepper. */
    stepCount: number;
    /** Array that contains the status of each step, the step number can be used as the index. */
    stepStatuses: StepperStepStatuses;
}

const StepperContext = createContext<StepperContextModel>({} as any);
export const useStepperContext = () => useContext(StepperContext);

export interface StepperContextStepModel
    extends Pick<StepperContextModel, "nextStep" | "previousStep" | "saveStep" | "stepCount">,
        StepperStepStatus {
    /** Function to edit the current step. */
    editStep: () => void;
    /** The current step number. */
    stepNumber: number;
}

/**
 * Hook that provides stepper context functionality for a specific step.
 *
 * @param stepNumber - Optional existing step number. If not provided, a new step will be automatically registered.
 */
export const useStepperContextStep = (stepNumber?: number): StepperContextStepModel => {
    const { editStep, nextStep, previousStep, registerStep, saveStep, stepCount, stepStatuses } = useStepperContext();
    const stepNumberRef = useRef(stepNumber ?? 0);

    useEffect(() => {
        if (!stepNumberRef.current && registerStep) {
            stepNumberRef.current = registerStep();
        }
    }, []);

    return {
        editStep: () => editStep(stepNumberRef.current),
        nextStep,
        previousStep,
        saveStep,
        stepCount,
        stepNumber: stepNumberRef.current,
        ...stepStatuses?.[stepNumberRef.current],
    };
};

export const StepperContextProvider: FC<PropsWithChildren> = ({ children }) => {
    const stepCountRef = useRef(0);
    const activeStepRef = useRef(0);
    const [stepStatuses, setStepStatuses] = useState<StepperStepStatuses>([null]);
    const { isEditing } = useShellContext();

    const editStep = (step: number) => {
        const activeStep = activeStepRef.current;
        const stepCount = stepCountRef.current;

        if (step <= 0 || step === activeStep || step > stepCount) {
            return;
        }

        // Set the step being edited to active and disable all other steps, then update statuses.
        const newStepStatuses: StepperStepStatuses = stepStatuses.map(x => {
            if (!x) {
                return null;
            }

            // A pending step does not need to be disabled.
            if (x.isPending) {
                return { isPending: true };
            }

            // Completed steps must remain completed while disabled.
            if (x.isComplete) {
                return { isComplete: true, isDisabled: true };
            }

            // Disable all other steps.
            return { isDisabled: true };
        });

        newStepStatuses[step] = { isActive: true, isEditing: true };

        activeStepRef.current = step;
        setStepStatuses(newStepStatuses);
    };

    const saveStep = () => {
        const activeStep = activeStepRef.current;

        if (activeStep <= 0) {
            return;
        }

        // Set complete status for the step being saved.
        const newStepStatuses = [...stepStatuses];
        newStepStatuses[activeStep] = { isComplete: true };

        // Set the next incomplete step to active, or unset active step (0) if all steps are complete.
        const nextIncompleteStep = newStepStatuses.findIndex(x => x && !x.isComplete);

        if (nextIncompleteStep !== -1) {
            activeStepRef.current = nextIncompleteStep;
            newStepStatuses[nextIncompleteStep] = { isActive: true };
        } else {
            activeStepRef.current = 0;
        }

        // Remove disabled status from all steps before the next incomplete step.
        newStepStatuses.some((x, index) => {
            if (!x) {
                return false;
            }

            if (!activeStepRef.current || index < activeStepRef.current) {
                delete x.isDisabled;

                return false;
            }

            return true;
        });

        setStepStatuses(newStepStatuses);
    };

    const nextStep = () => {
        const activeStep = activeStepRef.current;
        const stepCount = stepCountRef.current;
        const newStepStatuses = [...stepStatuses];

        // Set current active step to complete.
        if (activeStep > 0) {
            newStepStatuses[activeStep] = { isComplete: true };
        }

        // If the active step is the last step, unset active step (0) and update statuses.
        if (activeStep === stepCount) {
            activeStepRef.current = 0;
            setStepStatuses(newStepStatuses);

            return;
        }

        // Set the next step to active and update statuses.
        const nextStep = activeStep + 1;

        if (nextStep > stepCount) {
            return;
        }

        newStepStatuses[nextStep] = { isActive: true };

        activeStepRef.current = nextStep;
        setStepStatuses(newStepStatuses);
    };

    const previousStep = () => {
        const activeStep = activeStepRef.current;
        const previousStep = activeStep - 1;

        if (previousStep <= 0) {
            return;
        }

        // Set the active step to disabled and the previous step to active, then update statuses.
        const newStepStatuses = [...stepStatuses];
        newStepStatuses[activeStep] = { isDisabled: true };
        newStepStatuses[previousStep] = { isActive: true };

        activeStepRef.current = previousStep;
        setStepStatuses(newStepStatuses);
    };

    const registerStep = () => {
        const step = stepCountRef.current + 1;
        stepCountRef.current = step;

        const newStepStatus: StepperStepStatus = {};

        // If this is the first step being registered, set it to active, otherwise set all subsequent steps to pending.
        if (step === 1) {
            activeStepRef.current = step;
            newStepStatus.isActive = true;
        } else {
            newStepStatus.isPending = true;
        }

        setStepStatuses(x => [...x, newStepStatus]);

        return step;
    };

    return (
        <StepperContext.Provider
            value={{
                allStepsCompleted: stepStatuses.every(x => (x ? x.isComplete : true)),
                editStep,
                nextStep,
                previousStep,
                registerStep,
                saveStep,
                stepCount: stepCountRef.current,
                stepStatuses: isEditing ? stepStatuses.map(x => (x ? {} : null)) : stepStatuses,
            }}
        >
            {children}
        </StepperContext.Provider>
    );
};

export default StepperContext;
